import React, {useCallback, useMemo} from 'react';

import debounce from 'lodash.debounce';
import {SelectionHeader, SelectionRow} from 'src/components/data-table-selection';
import {UserLink} from 'src/components/links';
import {MainColumnCell, MainColumnHeader, getPaginatedRowNumber} from 'src/components/main-column';
import {ServerResponseHandler} from 'src/components/server-response-handler';
import {PlatformCell} from 'src/components/user-platform-column/platform-cell';
import {PLATFORM_COLUMN, PLATFORM_COLUMN_ID} from 'src/components/user-platform-column/platform-columns';
import {usePlatformColumn} from 'src/components/user-platform-column/use-platform-column';
import {ProductColumnHeader} from 'src/components/user-product-columns/product-column-header';
import {getProductAccessValue, isProductColumn} from 'src/components/user-product-columns/product-columns';
import {useProductColumns} from 'src/components/user-product-columns/product-columns-context';
import {useUserProductAccess} from 'src/components/user-product-columns/use-user-product-access';
import {MAX_SELECTED_ENTITIES, NO_RESULTS_MESSAGE, SEARCH_RESULT_HEADER_HEIGHT} from 'src/constants';
import {useDataTableColumnsWidthOnResize} from 'src/hooks/useDataTableColumnsWidthOnResize';
import {getUserLimit} from 'src/openapi/admin/apis/UserSearchLimitApi';
import {useAppDispatch} from 'src/store/hooks';

import {BoxLoading} from '@pb/kit-component-box-loading';
import {
    Cell,
    type CellOptions,
    type Column,
    DataTable,
    type HeaderCellOptions,
    type RowsParams,
    TableHeader,
} from '@pb/kit-component-data-table-v2';
import {FormattedDate} from '@pb/kit-component-formatters';

import {
    AccountCell,
    CreatedDateCell,
    CreatorCell,
    DealCloudIdCell,
    DefaultCell,
    GroupCell,
    HubspotIdCell,
    LastActivityCell,
    LastModificationReasonCell,
    LastModifiedDateCell,
    LcdSubscriptionNewsCell,
    LcdSubscriptionResearchCell,
    ModifierCell,
    MsDynamicsIdCell,
    NewslettersCell,
    SalesforceIdCell,
    SavedSearchesCell, TotalLimitCell, UsedLimitCell,
} from './components/cells';
import {
    AccountHeader,
    CreatedDateHeader,
    CreatorHeader,
    DealCloudIdHeader,
    EmailHeader,
    GroupHeader,
    HubspotIdHeader,
    JobTitleHeader,
    LastMobileActiveHeader,
    LastModificationReasonHeader,
    LastModifiedDateHeader,
    LastPlatformActiveHeader, LcdSubscriptionNewsHeader, LcdSubscriptionResearchHeader,
    LimitHeader,
    ModifierHeader,
    MsDynamicsIdHeader,
    NewslettersHeader,
    SalesforceIdHeader,
    SavedSearchesHeader,
    SortableHeader,
} from './components/headers';
import {UserSearchResultPagination} from './components/pagination';
import {
    COLUMNS,
    COLUMNS_DATA,
    SELECTION_KEY, SORTING_ERROR,
    UserActivitySource,
} from './constants';
import {useGetColumnValue} from './hooks/useGetColumnValue';
import {useGetUserSearchResult} from './hooks/useGetUserSearchResult';
import {getMappedLimitColumnId} from './utils';

import type {ChildrenProps as UserSearchControllerProps} from './user-search-result.controller';
import type {UserSearchTemplate} from 'src/openapi/admin/models/UserSearchTemplate';

type Props = {
    place: string,
    activeSearchTemplate: UserSearchTemplate,
    getAllEntityIds: UserSearchControllerProps['getAllEntityIds'],
};

export const UserSearchResultView = ({place, activeSearchTemplate, getAllEntityIds}: Props) => {
    const {getColumnValue} = useGetColumnValue();
    const apiDispatch = useAppDispatch();

    const {result, pagination, loadingError, cacheKeys, isLoading} = useGetUserSearchResult();
    const {getColumnWidthByColumnId, onStopResize} = useDataTableColumnsWidthOnResize();
    const {columnMap, headers: productHeaders, refreshVersion} = useProductColumns();
    const {enabled, columnAdapter} = usePlatformColumn();
    const columns = useMemo<Record<string, string>>(() => ({...COLUMNS, ...columnMap,
        ...enabled ? {[PLATFORM_COLUMN]: PLATFORM_COLUMN_ID} : {},
    }), [columnMap, enabled]);
    const visibleColumns = useMemo(() => Object.entries(columnAdapter.toDisplay(activeSearchTemplate.columns))
        .filter(([code]) => columns[code])
        .sort((a, b) => a[1] - b[1]), [activeSearchTemplate.columns, columns, columnAdapter]);
    const products = useUserProductAccess({
        userIds: result?.data.map(({id}) => id) || [],
        cacheKey: JSON.stringify(cacheKeys),
        enabled: !isLoading && visibleColumns.some(([code]) => isProductColumn(code)),
        refreshVersion,
    });


    const renderedRows = useMemo(() => {
        if (!result || !Array.isArray(result.data)) {
            return undefined;
        }

        return result.data.map(user => ({
            id: user.id,
            columnValues: visibleColumns
                .map(([code, order]) => ({code, order}))
                .sort((a, b) => a.order - b.order)
                .reduce((acc, column) => ({
                    ...acc,
                    [columns[column.code]]: isProductColumn(column.code)
                        ? getProductAccessValue(products.data || {}, user.id, column.code)
                        : getColumnValue(columns[column.code], user),
                }), {}),
        }));
    }, [result, visibleColumns, columns, products.data, getColumnValue]);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const loadTotalLimitDetails = useCallback(debounce(async (userId: number, columnId: string, cached: boolean) => {
        const limitTotalColumn = getMappedLimitColumnId(columnId);
        if (!limitTotalColumn || cached) {
            return;
        }

        await apiDispatch(getUserLimit({limitTotalColumn, userId}));
    }, 1000), [getMappedLimitColumnId, getUserLimit, apiDispatch]);

    const maxRowNumber = useMemo(() => (
        getPaginatedRowNumber({...pagination, index: renderedRows?.length})
    ), [pagination, renderedRows]);
    const rows = useMemo(() => result?.data.map(({id}) => id) || [], [result]);
    const totalCount = useMemo(() => result?.totalCount || 0, [result]);

    return (
        <ServerResponseHandler serverError={loadingError?.message === SORTING_ERROR ? null : loadingError}>
            <BoxLoading loading={!result}>
                <DataTable
                    data={{
                        rows: renderedRows,
                        columns: visibleColumns
                            .map(([code, order]) => ({code, order}))
                            .sort((a, b) => a.order - b.order)
                            .map(({code}) => {
                                const column =
                                    COLUMNS_DATA.find(({columnId}) => columns[code] === columnId) || {
                                        columnId: columns[code],
                                        fixed: false,
                                        defaultWidth: code === PLATFORM_COLUMN ? 260 : 200,
                                        minWidth: 200,
                                        maxWidth: 400,
                                    };

                                return ({
                                    ...column,
                                    width: getColumnWidthByColumnId(column.columnId),
                                });
                            }),
                    }}
                    getHeaderTopOffset={() => SEARCH_RESULT_HEADER_HEIGHT}
                    onStopResize={onStopResize}
                    headers={({column}: {column: Column}, cellOptions: HeaderCellOptions) => {
                        const wrapper = (children: React.ReactNode) => (
                            <Cell
                                {...cellOptions}
                                key={`${column.columnId}`}
                                column={column}
                                resizable={true}>
                                <TableHeader indent="none" className="search-header">
                                    {children}
                                </TableHeader>
                            </Cell>
                        );

                        if (isProductColumn(String(column.columnId))) {
                            return wrapper(
                                <ProductColumnHeader
                                    name={productHeaders[column.columnId]}
                                    loading={isLoading || products.loading}
                                    error={products.error}
                                />,
                            );
                        }

                        switch (column.columnId) {
                            case PLATFORM_COLUMN_ID: return wrapper(<strong>Platform</strong>);
                            case COLUMNS.EMAIL:
                                return (
                                    <Cell
                                        {...cellOptions}
                                        key={`${column.columnId}`}
                                        column={column}
                                        resizable={true}>
                                        <MainColumnHeader
                                            selection={(
                                                <SelectionHeader
                                                    selectionKey={SELECTION_KEY}
                                                    entityIds={rows}
                                                    totalCount={result?.totalCount || 0}
                                                    maxSelected={MAX_SELECTED_ENTITIES}
                                                    getEntityIds={getAllEntityIds}
                                                />
                                            )}
                                            maxRowNumber={maxRowNumber}>
                                            <EmailHeader/>
                                        </MainColumnHeader>
                                    </Cell>
                                );
                            case COLUMNS.ACCOUNT_ID: return wrapper(<AccountHeader/>);
                            case COLUMNS.GROUP_ID: return wrapper(<GroupHeader/>);
                            case COLUMNS.JOB_TITLE: return wrapper(<JobTitleHeader/>);
                            case COLUMNS.NEWSLETTERS: return wrapper(<NewslettersHeader/>);
                            case COLUMNS.LCD_SUBSCRIPTION_NEWS:
                                return wrapper(<LcdSubscriptionNewsHeader/>);
                            case COLUMNS.LCD_SUBSCRIPTION_RESEARCH:
                                return wrapper(<LcdSubscriptionResearchHeader/>);
                            case COLUMNS.LAST_PLATFORM_ACTIVITY: return wrapper(<LastPlatformActiveHeader/>);
                            case COLUMNS.LAST_MOBILE_ACTIVITY: return wrapper(<LastMobileActiveHeader/>);
                            case COLUMNS.SAVED_SEARCHES: return wrapper(<SavedSearchesHeader/>);
                            case COLUMNS.CREATOR: return wrapper(<CreatorHeader/>);
                            case COLUMNS.CREATED_DATE: return wrapper(<CreatedDateHeader/>);
                            case COLUMNS.LAST_MODIFIER: return wrapper(<ModifierHeader/>);
                            case COLUMNS.LAST_MODIFIED_DATE: return wrapper(<LastModifiedDateHeader/>);
                            case COLUMNS.LAST_MODIFICATION_REASON: return wrapper(
                                <LastModificationReasonHeader/>,
                            );
                            case COLUMNS.SALESFORCE_ID: return wrapper(<SalesforceIdHeader/>);
                            case COLUMNS.MS_DYNAMICS_ID: return wrapper(<MsDynamicsIdHeader/>);
                            case COLUMNS.DEAL_CLOUD_ID: return wrapper(<DealCloudIdHeader/>);
                            case COLUMNS.HUBSPOT_ID: return wrapper(<HubspotIdHeader/>);
                            case COLUMNS.VIEW_TOTAL:
                            case COLUMNS.M_VIEW_TOTAL:
                            case COLUMNS.PUBDD_TOTAL:
                            case COLUMNS.PUBDM_TOTAL:
                            case COLUMNS.PADD_TOTAL:
                            case COLUMNS.PMD_TOTAL:
                            case COLUMNS.CTDD_TOTAL:
                            case COLUMNS.CTMD_TOTAL:
                            case COLUMNS.DD_TOTAL:
                            case COLUMNS.DM_TOTAL:
                            case COLUMNS.PDD_TOTAL:
                            case COLUMNS.PDM_TOTAL:
                            case COLUMNS.IC_DD_TOTAL:
                            case COLUMNS.IC_MD_TOTAL:
                            case COLUMNS.BDCPU_DD_TOTAL:
                            case COLUMNS.BDCPU_MD_TOTAL:
                            case COLUMNS.MS_TOTAL:
                            case COLUMNS.IL_MD_TOTAL:
                            case COLUMNS.VIEW_USED:
                            case COLUMNS.M_VIEW_USED:
                            case COLUMNS.PUBDD_USED:
                            case COLUMNS.PUBDM_USED:
                            case COLUMNS.PADD_USED:
                            case COLUMNS.PMD_USED:
                            case COLUMNS.CTDD_USED:
                            case COLUMNS.CTMD_USED:
                            case COLUMNS.DD_USED:
                            case COLUMNS.DM_USED:
                            case COLUMNS.PDD_USED:
                            case COLUMNS.PDM_USED:
                            case COLUMNS.IC_DD_USED:
                            case COLUMNS.IC_MD_USED:
                            case COLUMNS.BDCPU_DD_USED:
                            case COLUMNS.BDCPU_MD_USED:
                            case COLUMNS.MS_USED:
                            case COLUMNS.CLOPHU_MD_USED:
                            case COLUMNS.CLOPHU_MD_TOTAL:
                            case COLUMNS.CLOTDU_MD_USED:
                            case COLUMNS.CLOTDU_MD_TOTAL:
                            case COLUMNS.IL_MD_USED:
                                return wrapper(<LimitHeader columnId={column.columnId}/>);
                            default: return wrapper(<SortableHeader columnId={column.columnId}/>);
                        }
                    }}
                    renderRows={(rowsParams: RowsParams, cellOptions: CellOptions) => {
                        const {rowIndex, column: {columnId}} = rowsParams;
                        const value = rowsParams.row.columnValues[columnId] as string;
                        const rowId = +rowsParams.row.id;
                        const column = rowsParams.column;
                        const cellKey = rowIndex + columnId.toString();
                        const rowNumber = getPaginatedRowNumber({...pagination, index: rowIndex + 1});

                        const wrapper = (children: React.ReactNode) => (
                            <Cell
                                {...cellOptions}
                                key={cellKey}
                                column={column}
                                className="search-result-cell">
                                {children}
                            </Cell>
                        );

                        switch (columnId) {
                            case PLATFORM_COLUMN_ID: {
                                const user = result!.data[rowIndex];
                                return wrapper(
                                    <PlatformCell
                                        type={String(getColumnValue(COLUMNS.TYPE, user))}
                                        status={String(getColumnValue(COLUMNS.STATUS, user))}
                                        statusCode={user.status}
                                    />,
                                );
                            }
                            case COLUMNS.EMAIL:
                                return (
                                    <MainColumnCell
                                        key={cellKey}
                                        {...{
                                            selection: (
                                                <SelectionRow
                                                    rows={rows}
                                                    index={rowIndex}
                                                    entityId={rowId}
                                                    selectionKey={SELECTION_KEY}
                                                    totalCount={totalCount}
                                                />
                                            ),
                                            rowId,
                                            maxRowNumber,
                                            rowNumber,
                                            cellOptions: {...cellOptions, column},
                                        }}>
                                        <UserLink userId={+rowId} place={place} caption={value as string}/>
                                    </MainColumnCell>
                                );
                            case COLUMNS.ACCOUNT_ID: return wrapper(
                                <AccountCell place={place} accountId={Number(value)}/>,
                            );
                            case COLUMNS.GROUP_ID: return wrapper(<GroupCell groupId={String(value)}/>);
                            case COLUMNS.NEWSLETTERS: return wrapper(<NewslettersCell userId={rowId}/>);
                            case COLUMNS.LCD_SUBSCRIPTION_NEWS:
                                return wrapper(<LcdSubscriptionNewsCell userId={rowId}/>);
                            case COLUMNS.LCD_SUBSCRIPTION_RESEARCH:
                                return wrapper(<LcdSubscriptionResearchCell userId={rowId}/>);
                            case COLUMNS.LAST_PLATFORM_ACTIVITY: return wrapper(
                                <LastActivityCell
                                    userId={+rowId}
                                    activitySource={UserActivitySource.PLATFORM}
                                />,
                            );
                            case COLUMNS.LAST_MOBILE_ACTIVITY: return wrapper(
                                <LastActivityCell
                                    userId={+rowId}
                                    activitySource={UserActivitySource.MOBILE}
                                />,
                            );
                            case COLUMNS.FIRST_LICENSE_DATE: return wrapper(
                                <span>
                                    {value ? (
                                        FormattedDate.format({value, format: 'MMM DD, YYYY'})
                                    ) : ''}
                                </span>,
                            );
                            case COLUMNS.SAVED_SEARCHES: return wrapper(<SavedSearchesCell userId={+rowId}/>);
                            case COLUMNS.CREATOR: return wrapper(<CreatorCell place={place} userId={+rowId}/>);
                            case COLUMNS.CREATED_DATE: return wrapper(<CreatedDateCell userId={+rowId}/>);
                            case COLUMNS.LAST_MODIFIER: return wrapper(<ModifierCell place={place} userId={+rowId}/>);
                            case COLUMNS.LAST_MODIFICATION_REASON: return wrapper(
                                <LastModificationReasonCell userId={rowId}/>,
                            );
                            case COLUMNS.LAST_MODIFIED_DATE: return wrapper(
                                <LastModifiedDateCell userId={+rowId}/>,
                            );
                            case COLUMNS.SALESFORCE_ID: return wrapper(<SalesforceIdCell userId={+rowId}/>);
                            case COLUMNS.MS_DYNAMICS_ID: return wrapper(<MsDynamicsIdCell userId={+rowId}/>);
                            case COLUMNS.DEAL_CLOUD_ID: return wrapper(<DealCloudIdCell userId={+rowId}/>);
                            case COLUMNS.HUBSPOT_ID: return wrapper(<HubspotIdCell userId={+rowId}/>);
                            case COLUMNS.VIEW_TOTAL:
                            case COLUMNS.M_VIEW_TOTAL:
                            case COLUMNS.PUBDD_TOTAL:
                            case COLUMNS.PUBDM_TOTAL:
                            case COLUMNS.PADD_TOTAL:
                            case COLUMNS.PMD_TOTAL:
                            case COLUMNS.CTDD_TOTAL:
                            case COLUMNS.CTMD_TOTAL:
                            case COLUMNS.DD_TOTAL:
                            case COLUMNS.DM_TOTAL:
                            case COLUMNS.PDD_TOTAL:
                            case COLUMNS.PDM_TOTAL:
                            case COLUMNS.IC_DD_TOTAL:
                            case COLUMNS.IC_MD_TOTAL:
                            case COLUMNS.BDCPU_DD_TOTAL:
                            case COLUMNS.BDCPU_MD_TOTAL:
                            case COLUMNS.MS_TOTAL:
                            case COLUMNS.IL_MD_TOTAL:
                            case COLUMNS.CLOPHU_MD_TOTAL:
                            case COLUMNS.CLOTDU_MD_TOTAL:
                                return wrapper(
                                    <TotalLimitCell
                                        columnId={columnId}
                                        userId={+rowId}
                                        onHover={loadTotalLimitDetails}
                                    />,
                                );
                            case COLUMNS.VIEW_USED:
                            case COLUMNS.M_VIEW_USED:
                            case COLUMNS.PUBDD_USED:
                            case COLUMNS.PUBDM_USED:
                            case COLUMNS.PADD_USED:
                            case COLUMNS.PMD_USED:
                            case COLUMNS.CTDD_USED:
                            case COLUMNS.CTMD_USED:
                            case COLUMNS.DD_USED:
                            case COLUMNS.DM_USED:
                            case COLUMNS.PDD_USED:
                            case COLUMNS.PDM_USED:
                            case COLUMNS.IC_DD_USED:
                            case COLUMNS.IC_MD_USED:
                            case COLUMNS.BDCPU_DD_USED:
                            case COLUMNS.BDCPU_MD_USED:
                            case COLUMNS.MS_USED:
                            case COLUMNS.IL_MD_USED:
                            case COLUMNS.CLOPHU_MD_USED:
                            case COLUMNS.CLOTDU_MD_USED:
                                return wrapper(<UsedLimitCell columnId={columnId} userId={+rowId}/>);
                            default: return wrapper(<DefaultCell value={String(value)}/>);
                        }
                    }}
                    paginationStripe={(
                        <UserSearchResultPagination totalCount={result?.totalCount || 0}/>
                    )}
                    showNoDataMsg={!rows?.length}
                    tableMessage={NO_RESULTS_MESSAGE}
                />
            </BoxLoading>
        </ServerResponseHandler>
    );
};
